import { useEffect, useMemo, useState } from 'react';

import { SelectOption } from '@/shared/types';

import { BlockTitle, FormContent, FormRow, FormElement, FormInputWrapper } from '@/components/old';

import { getIndividualStatusSelect } from '@/api/apiService';
import { DocumentTypeCode, DocumentTypeEntity } from '@/api/types';

import { FormConstants, PersonStatusFieldName } from '../../../../../../../components/Forms/constants';
import FileUpload from '../../../../../../../components/Forms/FileUpload';
import InitialSelect from '../../../../../../../components/Forms/Select';
import InputDate from '../../../../../../../components/Inputs/InputDate';
import { StepsNavigationName } from '../../../../../../../config/services';
import { getERZDocumentsSelect } from '../../../../../helpers/getERZDocumentsSelect';

import { PersonStatusFormDataProps } from './types';

interface Props {
  formData: PersonStatusFormDataProps;
  handleInputChange: (name: string, value: unknown) => void;
  handleClearInput: (name: string) => void;
}

const Step5: React.FC<Props> = ({ formData, handleInputChange, handleClearInput }) => {
  const [optionsPersonStatus, setOptionsPersonStatus] = useState<SelectOption[]>([]);
  const [optionsPersonDoc, setOptionsPersonDoc] = useState<DocumentTypeEntity[]>([]);

  const personAddDeclaredDoc = useMemo(() => formData.personAddDeclaredDoc || false, [formData.personAddDeclaredDoc]);
  const personStatusName = useMemo(
    () => (formData.personStatusName ? JSON.parse(formData.personStatusName) : null),
    [formData.personStatusName],
  );
  const personStatusDocType = useMemo(
    () => (formData.personStatusDocType ? JSON.parse(formData.personStatusDocType) : null),
    [formData.personStatusDocType],
  );
  const personStatusDocIssueDate = useMemo(
    () => (formData.personStatusDocIssueDate ? new Date(formData.personStatusDocIssueDate) : null),
    [formData.personStatusDocIssueDate],
  );
  const personStatusDocExpireDate = useMemo(
    () => (formData.personStatusDocExpireDate ? new Date(formData.personStatusDocExpireDate) : null),
    [formData.personStatusDocExpireDate],
  );

  const handleInputsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    handleInputChange(name, value);
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    const booleanValue = value.toLowerCase() === 'true';

    if (!booleanValue) {
      [
        PersonStatusFieldName.personStatusDocType,
        PersonStatusFieldName.personStatusName,
        PersonStatusFieldName.personStatusOrg,
        PersonStatusFieldName.personStatusDocSerialNumber,
        PersonStatusFieldName.personStatusDocIssuingAuthority,
        PersonStatusFieldName.personStatusDocIssueDate,
        PersonStatusFieldName.personStatusDocExpireDate,
        PersonStatusFieldName.personStatusDocument,
      ].forEach(handleClearInput);
    }

    handleInputChange(name, booleanValue);
  };

  useEffect(() => {
    if (personAddDeclaredDoc) {
      (async (): Promise<void> => {
        if (!formData.personStatusDocType) {
          const [document] = await getERZDocumentsSelect({
            allowedDocumentTypeCodes: [DocumentTypeCode.CERTIFICATION],
            setOptions: setOptionsPersonDoc,
          });
          handleInputChange(PersonStatusFieldName.personStatusDocType, document);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personAddDeclaredDoc, personStatusDocType]);

  return (
    <>
      <BlockTitle id="step-description" paddingBottomMdExtra bgLight>
        {StepsNavigationName.personStatus}
      </BlockTitle>

      <FormContent>
        <FormRow>
          <FormInputWrapper
            label="Ви хочете додати дані документа?"
            marginBottomNone
            labelClassName="form-input__label_margin-bottom-md"
          >
            <fieldset id={PersonStatusFieldName.personAddDeclaredDoc} className="form-input__radiobuttons">
              <label className="radio" htmlFor={PersonStatusFieldName.personAddDeclaredDoc + '_yes'}>
                <input
                  className="radio__input"
                  type="radio"
                  id={PersonStatusFieldName.personAddDeclaredDoc + '_yes'}
                  name={PersonStatusFieldName.personAddDeclaredDoc}
                  value="true"
                  checked={personAddDeclaredDoc}
                  onChange={handleRadioChange}
                />
                <span className="radio__checkmark"></span>
                <span className="radio__label">Так</span>
              </label>

              <label className="radio" htmlFor={PersonStatusFieldName.personAddDeclaredDoc + '_no'}>
                <input
                  className="radio__input"
                  type="radio"
                  id={PersonStatusFieldName.personAddDeclaredDoc + '_no'}
                  name={PersonStatusFieldName.personAddDeclaredDoc}
                  value="false"
                  checked={!personAddDeclaredDoc}
                  onChange={handleRadioChange}
                />
                <span className="radio__checkmark"></span>
                <span className="radio__label">Ні</span>
              </label>
            </fieldset>
          </FormInputWrapper>
        </FormRow>

        {personAddDeclaredDoc && (
          <>
            <FormInputWrapper label="Назва статусу особи" required marginBottomNone>
              <div className="input-select js-input-select">
                <InitialSelect
                  name={PersonStatusFieldName.personStatusName}
                  options={optionsPersonStatus}
                  placeholder="Не вибрано"
                  defaultValue={personStatusName}
                  onChange={(newValue, name): void => handleInputChange(name, newValue)}
                  onMenuOpen={(): void => {
                    if (!optionsPersonStatus.length) {
                      getIndividualStatusSelect({ option: setOptionsPersonStatus });
                    }
                  }}
                />
              </div>
            </FormInputWrapper>

            <FormElement variant="title">Дані документа, що підтверджує статус особи</FormElement>

            <FormInputWrapper
              label="Тип документа, що підтверджує статус особи"
              required
              marginBottomNone
              className="no-group"
            >
              <div className="input-select js-input-select">
                <InitialSelect
                  name={PersonStatusFieldName.personStatusDocType}
                  options={optionsPersonDoc}
                  placeholder="Не вибрано"
                  defaultValue={personStatusDocType}
                  onChange={(newValue, name): void => handleInputChange(name, newValue)}
                  onMenuOpen={(): void => {
                    if (!optionsPersonDoc.length) {
                      getERZDocumentsSelect({
                        allowedDocumentTypeCodes: [DocumentTypeCode.CERTIFICATION],
                        setOptions: setOptionsPersonDoc,
                      });
                    }
                  }}
                />
              </div>
            </FormInputWrapper>

            <FormInputWrapper label="Назва організації" required marginBottomNone>
              <input
                className="form-input__input"
                placeholder="Не вказано"
                type="text"
                name={PersonStatusFieldName.personStatusOrg}
                value={formData.personStatusOrg || ''}
                onChange={handleInputsChange}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormInputWrapper>

            <FormInputWrapper label="Серія та номер" required marginBottomNone>
              <input
                className="form-input__input"
                placeholder="Не вказано"
                type="text"
                name={PersonStatusFieldName.personStatusDocSerialNumber}
                value={formData.personStatusDocSerialNumber || ''}
                onChange={handleInputsChange}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormInputWrapper>

            <FormInputWrapper label="Орган видачі" required marginBottomNone>
              <input
                className="form-input__input"
                placeholder="Не вказано"
                type="text"
                name={PersonStatusFieldName.personStatusDocIssuingAuthority}
                value={formData.personStatusDocIssuingAuthority || ''}
                onChange={handleInputsChange}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormInputWrapper>

            <FormRow>
              <InputDate
                required
                minDateBirth
                maxDateToday
                label="Дата видачі"
                name={PersonStatusFieldName.personStatusDocIssueDate}
                value={personStatusDocIssueDate}
                onChange={(date): void => handleInputChange(PersonStatusFieldName.personStatusDocIssueDate, date)}
              />
              <InputDate
                required
                minDateToday
                label="Дійсний до"
                name={PersonStatusFieldName.personStatusDocExpireDate}
                value={personStatusDocExpireDate}
                onChange={(date): void => handleInputChange(PersonStatusFieldName.personStatusDocExpireDate, date)}
              />
            </FormRow>
            <FormInputWrapper label="Документ, що підтверджує статус особи" required marginBottomNone>
              <FileUpload
                inputName={PersonStatusFieldName.personStatusDocument}
                inputTitle="Додати сканкопію документа"
                value={formData.personStatusDocument}
                handleInputChange={handleInputChange}
                handleClearInput={handleClearInput}
              />
            </FormInputWrapper>
          </>
        )}
      </FormContent>
    </>
  );
};

export default Step5;
